import { action, observable, makeObservable, computed, reaction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { apiGetAccountLicenseConfigRequest } from 'src/api_openapi/v2/compliance/apiGetAccountLicenseConfig';
import { apiSetAccountLicenseConfigRequest } from 'src/api_openapi/v2/compliance/apiSetAccountLicenseConfig';
import {
    ComplianceLicenseAccountConfigInnerType,
    ComplianceLicenseAccountConfigInfoUpdateRequestType,
    ComplianceCurrencyType,
} from 'src/api_openapi/v2/compliance/schemas';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    validateAmountOrNothing,
    validatePositiveOrNothing,
} from 'src/domains/players/shared/validatorsNew/validateRequire';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { Amount } from 'src_common/common/amount/Amount';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';

const isComplianceCurrency = (value: string): value is ComplianceCurrencyType => {
    return value === 'EUR' || value === 'GBP' || value === 'XTS';
};

export class AccountConfigState {
    private readonly configsResource: Resource<ComplianceLicenseAccountConfigInnerType[]>;
    private readonly disposers: Array<() => void> = [];
    @observable public selectedCurrency: ComplianceCurrencyType | null = null;
    @observable public selectedAccountId: number;
    @observable public isSaving: boolean = false;
    @observable public errorMessage: string | null = null;
    public readonly currency: FormInputState<string, string>;
    public readonly maxSingleWithdrawalTrigger: FormInputState<string, Amount | string>;
    public readonly maxSingleDepositTrigger: FormInputState<string, Amount | string>;
    public readonly maxTotalWithdrawalTriggerUnderThreshold: FormInputState<string, Amount | string>;
    public readonly maxTotalWithdrawalTriggerOverThreshold: FormInputState<string, Amount | string>;
    public readonly maxTotalDepositTriggerUnderThreshold: FormInputState<string, Amount | string>;
    public readonly maxTotalDepositTriggerOverThreshold: FormInputState<string, Amount | string>;
    public readonly drMaxTotalWithdrawalTriggerUnderThreshold: FormInputState<string, Amount | string>;
    public readonly drMaxTotalWithdrawalTriggerOverThreshold: FormInputState<string, Amount | string>;
    public readonly drMaxTotalDepositTriggerUnderThreshold: FormInputState<string, Amount | string>;
    public readonly drMaxTotalDepositTriggerOverThreshold: FormInputState<string, Amount | string>;

    public constructor(
        private readonly appState: AppState,
        customerId: number
    ) {
        this.selectedAccountId = customerId;

        this.currency = FormInputState.new('').map(validateNotEmpty('Please select currency.'));
        this.maxSingleWithdrawalTrigger = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.maxSingleDepositTrigger = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.maxTotalWithdrawalTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.maxTotalWithdrawalTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.maxTotalDepositTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.maxTotalDepositTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.drMaxTotalWithdrawalTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.drMaxTotalWithdrawalTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.drMaxTotalDepositTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);
        this.drMaxTotalDepositTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountOrNothing)
            .map(validatePositiveOrNothing);

        this.configsResource = new Resource(async () => {
            const { gatewayUrl } = this.appState.common.appEnvParams;
            const response = await apiGetAccountLicenseConfigRequest(
                gatewayUrl,
                { accountId: this.selectedAccountId },
                { authorization: `Bearer ${this.appState.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                return response.body.config;
            } else {
                throw new Error(`Failed to fetch config: status ${response.status}`);
            }
        });

        makeObservable(this);

        this.disposers.push(
            reaction(
                () => ({ isEmptyConfig: this.isEmptyConfig, walletCurrency: this.walletCurrency }),
                ({ isEmptyConfig, walletCurrency }) => {
                    if (isEmptyConfig === true && walletCurrency !== null && this.currency.value === '') {
                        this.currency.setValue(walletCurrency);
                    }
                },
                { fireImmediately: true }
            )
        );

        this.disposers.push(
            reaction(
                () => this.config,
                (config) => {
                    this.loadConfigToForm(config);
                    if (config?.currency !== undefined && this.selectedCurrency !== config.currency) {
                        this.selectCurrency(config.currency);
                    }
                },
                { fireImmediately: true }
            )
        );

        this.disposers.push(
            reaction(
                () => this.configsResourceError,
                (error) => {
                    if (error !== null) {
                        this.setError(error);
                    }
                },
                { fireImmediately: true }
            )
        );
    }

    public dispose(): void {
        this.disposers.forEach((dispose) => dispose());
    }

    @computed public get walletCurrency(): CurrencyType | null {
        if (this.isEmptyConfig === false) {
            return null;
        }

        return this.appState.models.getUserModel(this.selectedAccountId).walletData.data?.currency ?? null;
    }

    @computed public get config(): ComplianceLicenseAccountConfigInnerType | null {
        const result = this.configsResource.get();

        if (result.type === 'ready') {
            const firstConfig = result.value[0];
            if (firstConfig !== undefined && firstConfig.currency !== undefined) {
                return firstConfig;
            }
            if (result.value.length === 0) {
                return this.getEmptyConfig();
            }
            return null;
        }

        return null;
    }

    @computed private get configsResourceError(): string | null {
        const result = this.configsResource.get();
        return result.type === 'error' ? result.message : null;
    }

    private getEmptyConfig(): ComplianceLicenseAccountConfigInnerType {
        return {
            currency: undefined,
            maxSingleWithdrawalTrigger: '',
            maxSingleDepositTrigger: '',
            maxTotalWithdrawalTriggerUnderThreshold: '',
            maxTotalWithdrawalTriggerOverThreshold: '',
            maxTotalDepositTriggerUnderThreshold: '',
            maxTotalDepositTriggerOverThreshold: '',
            drMaxTotalWithdrawalTriggerUnderThreshold: '',
            drMaxTotalWithdrawalTriggerOverThreshold: '',
            drMaxTotalDepositTriggerUnderThreshold: '',
            drMaxTotalDepositTriggerOverThreshold: '',
        };
    }

    @computed public get isEmptyConfig(): boolean {
        return this.config !== null && this.config.currency === undefined;
    }

    @computed public get isLoading(): boolean {
        return this.configsResource.get().type === 'loading';
    }

    public async refresh(): Promise<void> {
        await this.configsResource.refresh();
    }

    @action public selectCurrency = (currency: ComplianceCurrencyType): void => {
        this.selectedCurrency = currency;
    };

    @action public loadConfigToForm = (config: ComplianceLicenseAccountConfigInnerType | null): void => {
        if (config === null) {
            this.resetForm();
            return;
        }

        this.currency.setValue(config.currency ?? '');
        this.maxSingleWithdrawalTrigger.setValue(config.maxSingleWithdrawalTrigger ?? '');
        this.maxSingleDepositTrigger.setValue(config.maxSingleDepositTrigger ?? '');
        this.maxTotalWithdrawalTriggerUnderThreshold.setValue(config.maxTotalWithdrawalTriggerUnderThreshold ?? '');
        this.maxTotalWithdrawalTriggerOverThreshold.setValue(config.maxTotalWithdrawalTriggerOverThreshold ?? '');
        this.maxTotalDepositTriggerUnderThreshold.setValue(config.maxTotalDepositTriggerUnderThreshold ?? '');
        this.maxTotalDepositTriggerOverThreshold.setValue(config.maxTotalDepositTriggerOverThreshold ?? '');
        this.drMaxTotalWithdrawalTriggerUnderThreshold.setValue(config.drMaxTotalWithdrawalTriggerUnderThreshold ?? '');
        this.drMaxTotalWithdrawalTriggerOverThreshold.setValue(config.drMaxTotalWithdrawalTriggerOverThreshold ?? '');
        this.drMaxTotalDepositTriggerUnderThreshold.setValue(config.drMaxTotalDepositTriggerUnderThreshold ?? '');
        this.drMaxTotalDepositTriggerOverThreshold.setValue(config.drMaxTotalDepositTriggerOverThreshold ?? '');
    };

    @action public resetForm = (): void => {
        this.currency.reset();
        this.maxSingleWithdrawalTrigger.reset();
        this.maxSingleDepositTrigger.reset();
        this.maxTotalWithdrawalTriggerUnderThreshold.reset();
        this.maxTotalWithdrawalTriggerOverThreshold.reset();
        this.maxTotalDepositTriggerUnderThreshold.reset();
        this.maxTotalDepositTriggerOverThreshold.reset();
        this.drMaxTotalWithdrawalTriggerUnderThreshold.reset();
        this.drMaxTotalWithdrawalTriggerOverThreshold.reset();
        this.drMaxTotalDepositTriggerUnderThreshold.reset();
        this.drMaxTotalDepositTriggerOverThreshold.reset();
    };

    @action private setError = (message: string | null): void => {
        this.errorMessage = message;
    };

    @action private setSaving = (saving: boolean): void => {
        this.isSaving = saving;
    };

    @action public updateConfig = async (): Promise<void> => {
        this.currency.setAsVisited();
        this.maxSingleWithdrawalTrigger.setAsVisited();
        this.maxSingleDepositTrigger.setAsVisited();
        this.maxTotalWithdrawalTriggerUnderThreshold.setAsVisited();
        this.maxTotalWithdrawalTriggerOverThreshold.setAsVisited();
        this.maxTotalDepositTriggerUnderThreshold.setAsVisited();
        this.maxTotalDepositTriggerOverThreshold.setAsVisited();
        this.drMaxTotalWithdrawalTriggerUnderThreshold.setAsVisited();
        this.drMaxTotalWithdrawalTriggerOverThreshold.setAsVisited();
        this.drMaxTotalDepositTriggerUnderThreshold.setAsVisited();
        this.drMaxTotalDepositTriggerOverThreshold.setAsVisited();

        if (
            this.currency.result.value.type === 'error' ||
            this.maxSingleWithdrawalTrigger.result.value.type === 'error' ||
            this.maxSingleDepositTrigger.result.value.type === 'error' ||
            this.maxTotalWithdrawalTriggerUnderThreshold.result.value.type === 'error' ||
            this.maxTotalWithdrawalTriggerOverThreshold.result.value.type === 'error' ||
            this.maxTotalDepositTriggerUnderThreshold.result.value.type === 'error' ||
            this.maxTotalDepositTriggerOverThreshold.result.value.type === 'error' ||
            this.drMaxTotalWithdrawalTriggerUnderThreshold.result.value.type === 'error' ||
            this.drMaxTotalWithdrawalTriggerOverThreshold.result.value.type === 'error' ||
            this.drMaxTotalDepositTriggerUnderThreshold.result.value.type === 'error' ||
            this.drMaxTotalDepositTriggerOverThreshold.result.value.type === 'error'
        ) {
            this.setError('Please fix all validation errors');
            return;
        }

        try {
            this.setSaving(true);
            this.setError(null);

            if (this.currency.value === '' || !isComplianceCurrency(this.currency.value)) {
                this.setError('Currency is required');
                return;
            }

            const toOptionalString = (value: string): string | undefined => (value === '' ? undefined : value);

            const updatedConfig: ComplianceLicenseAccountConfigInfoUpdateRequestType = {
                currency: this.currency.value,
                maxSingleWithdrawalTrigger: toOptionalString(this.maxSingleWithdrawalTrigger.value),
                maxSingleDepositTrigger: toOptionalString(this.maxSingleDepositTrigger.value),
                maxTotalWithdrawalTriggerUnderThreshold: toOptionalString(
                    this.maxTotalWithdrawalTriggerUnderThreshold.value
                ),
                maxTotalWithdrawalTriggerOverThreshold: toOptionalString(
                    this.maxTotalWithdrawalTriggerOverThreshold.value
                ),
                maxTotalDepositTriggerUnderThreshold: toOptionalString(this.maxTotalDepositTriggerUnderThreshold.value),
                maxTotalDepositTriggerOverThreshold: toOptionalString(this.maxTotalDepositTriggerOverThreshold.value),
                drMaxTotalWithdrawalTriggerUnderThreshold: toOptionalString(
                    this.drMaxTotalWithdrawalTriggerUnderThreshold.value
                ),
                drMaxTotalWithdrawalTriggerOverThreshold: toOptionalString(
                    this.drMaxTotalWithdrawalTriggerOverThreshold.value
                ),
                drMaxTotalDepositTriggerUnderThreshold: toOptionalString(
                    this.drMaxTotalDepositTriggerUnderThreshold.value
                ),
                drMaxTotalDepositTriggerOverThreshold: toOptionalString(
                    this.drMaxTotalDepositTriggerOverThreshold.value
                ),
            };

            const { gatewayUrl } = this.appState.common.appEnvParams;
            const response = await apiSetAccountLicenseConfigRequest(
                gatewayUrl,
                {
                    accountId: this.selectedAccountId,
                    requestBody: updatedConfig,
                },
                { authorization: `Bearer ${this.appState.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                await this.refresh();
            } else {
                this.setError(`Failed to update config: status ${response.status}`);
            }
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Unknown error occurred');
            console.error('Error updating account config:', error);
        } finally {
            this.setSaving(false);
        }
    };

    @computed public get selectedConfig(): ComplianceLicenseAccountConfigInnerType | null {
        return this.config;
    }
}
